/**
 * One question: a -3..+3 slider, and how much it should count.
 *
 * Rendered from data alone — prompt, two pole labels and a help string — so
 * questions added in /admin need no code here.
 *
 * The control is a real <input type="range"> with a transparent track, drawn
 * over a track we paint ourselves. That keeps dragging, track clicks, arrow
 * keys, Home/End, touch and screen-reader support for free, while letting the
 * fill run outward from the centre rather than up from the left — which is the
 * whole point of a scale whose middle means "don't care" rather than "half".
 *
 * Every hit target here is at least 44px on its shortest axis, including on
 * desktop. With a hundred-odd of these on one page, a control that needs to be
 * aimed at is a control that gets answered wrongly.
 *
 * PERFORMANCE
 * -----------
 * There are ~140 of these mounted at once, so this component is memoised and
 * every prop it takes is a primitive or a stable identity. That's why the
 * callbacks are `(questionId, value)` rather than the more natural `(value)`:
 * a parent can then hand the *same* function to all 140 instances instead of
 * minting 140 closures on every keystroke, which would defeat the memo and
 * re-render the entire page for one slider moving.
 */

import { Link2, Minus, Star, TrendingDown, X } from "lucide-react";
import { memo, useCallback, useId } from "react";

import { cn } from "@/lib/utils";
import { ladderLabel, ladderPoles } from "@/shared/ladders";
import {
  IMPORTANCE_LABELS,
  SCALE_MAX,
  SCALE_MIN,
  UNANSWERED_LABEL,
  type Importance,
  type ScaleValue,
} from "@/shared/scale";
import type { Question } from "@/shared/types";

const STOPS: ScaleValue[] = [-3, -2, -1, 0, 1, 2, 3];

/** Percentage along the track for a value, 0..100. */
const positionOf = (v: number) => ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;

/** Precomputed: these never change, so they don't belong in the render path. */
const STOP_POSITIONS = STOPS.map(positionOf);

function colorFor(value: ScaleValue): string {
  if (value === 0) return "var(--muted-foreground)";
  return value < 0 ? "var(--negative)" : "var(--positive)";
}

// ---------------------------------------------------------------------------

const IMPORTANCE_OPTIONS: {
  v: Importance;
  icon: typeof Star;
  label: string;
  key: string;
}[] = [
  { v: -1, icon: TrendingDown, label: IMPORTANCE_LABELS[-1], key: "−" },
  { v: 0, icon: Minus, label: IMPORTANCE_LABELS[0], key: "*" },
  { v: 1, icon: Star, label: IMPORTANCE_LABELS[1], key: "+" },
];

const ImportanceControl = memo(function ImportanceControl({
  value,
  disabled,
  onChange,
}: {
  value: Importance;
  disabled: boolean;
  onChange: (v: Importance) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="How much this question should count"
      className={cn(
        "flex shrink-0 items-center gap-0.5 rounded-full border border-border/70 bg-card/60 p-1 transition-opacity",
        disabled && "pointer-events-none opacity-30",
      )}
    >
      {IMPORTANCE_OPTIONS.map(({ v, icon: Icon, label, key }) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={`${label} (${key})`}
            title={`${label}  ·  ${key}`}
            data-importance={v}
            onClick={() => onChange(v)}
            className={cn(
              "grid size-8 place-items-center rounded-full transition-colors",
              active
                ? v === 1
                  ? "bg-brand text-primary-foreground"
                  : v === -1
                    ? "bg-muted-foreground/25 text-foreground"
                    : "bg-accent text-accent-foreground"
                : "text-muted-foreground/50 hover:bg-accent/60 hover:text-foreground",
            )}
          >
            <Icon className={cn("size-3.5", active && v === 1 && "fill-current")} />
          </button>
        );
      })}
    </div>
  );
});

// ---------------------------------------------------------------------------

export interface ScaleSliderProps {
  question: Question;
  /** undefined means unanswered, which is not the same as 0. */
  value: ScaleValue | undefined;
  importance: Importance;
  /** Highlighted by keyboard navigation. */
  active: boolean;
  /**
   * The prompt of the question this one is scored against on the other side,
   * already resolved. A string rather than the question, because the memo
   * above only holds if every prop is a primitive or a stable identity.
   */
  linkedPrompt: string | null;
  /** All five take the question id so one instance can serve every row. */
  onChange: (questionId: string, value: ScaleValue) => void;
  onClear: (questionId: string) => void;
  onImportance: (questionId: string, importance: Importance) => void;
  onActivate: (questionId: string) => void;
  onRegister: (questionId: string, el: HTMLInputElement | null) => void;
}

export const ScaleSlider = memo(function ScaleSlider({
  question,
  value,
  importance,
  active,
  linkedPrompt,
  onChange,
  onClear,
  onImportance,
  onActivate,
  onRegister,
}: ScaleSliderProps) {
  const id = useId();
  const questionId = question.id;

  const answered = value !== undefined;
  const shown = value ?? 0;
  const pos = positionOf(shown);
  const valueLabel = ladderLabel(question.ladder, shown);
  const [negativeLabel, positiveLabel] = ladderPoles(question.ladder);
  const color = colorFor(shown);

  // Bound once per instance rather than once per parent render, so the memo
  // above actually holds and the ref isn't detached and reattached each time.
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(questionId, Number(e.target.value) as ScaleValue),
    [onChange, questionId],
  );
  const handleClear = useCallback(() => onClear(questionId), [onClear, questionId]);
  const handleImportance = useCallback(
    (v: Importance) => onImportance(questionId, v),
    [onImportance, questionId],
  );
  const handleActivate = useCallback(
    () => onActivate(questionId),
    [onActivate, questionId],
  );
  const ref = useCallback(
    (el: HTMLInputElement | null) => onRegister(questionId, el),
    [onRegister, questionId],
  );

  return (
    <div
      // Keyboard nav scrolls *this* element rather than the range input inside
      // it, so the whole highlighted row is what gets brought into view.
      data-question-row=""
      // Both, and in this order, on purpose. pointerdown paints the highlight
      // at once, but any focus taken there is immediately undone: the browser's
      // own default for a press on non-focusable markup is to move focus to the
      // body, and that default runs after this handler. click fires after it
      // has, so that's where the focus survives — and focus is what makes
      // ← / → work on the row you just clicked.
      onPointerDown={handleActivate}
      onClick={handleActivate}
      className={cn(
        // Negative inline margin so the highlight bleeds to the card edge
        // rather than sitting in a box inset from it.
        // The scroll margins keep the row clear of the viewport edges when the
        // browser scrolls to it on its own — tabbing between inputs — with -mt
        // covering the sticky app bar. Arrow-key navigation doesn't rely on
        // them; it measures the bar and scrolls the row itself (lib/keyboard).
        "-mx-3 scroll-mb-6 scroll-mt-20 border-b border-border/50 px-3 py-4 transition-colors last:border-0 sm:-mx-4 sm:px-4",
        active && "rounded-xl border-transparent bg-brand/[0.06]",
      )}
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-40">
          <label
            htmlFor={id}
            className="text-[15px] font-medium leading-snug tracking-[-0.01em]"
          >
            {question.prompt}
          </label>
          {question.help && (
            <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
              {question.help}
            </p>
          )}
          {linkedPrompt && (
            /* Said plainly, because it changes what the answer means: sliding
               this one to "Love" is not a claim to have something in common
               with everyone else who did. Rendered as text rather than a
               tooltip so it survives a screen reader and a narrow phone. */
            <p className="mt-1.5 inline-flex items-start gap-1.5 rounded-lg bg-brand/[0.07] px-2 py-1 text-[12px] leading-snug text-brand">
              <Link2 className="mt-px size-3 shrink-0" aria-hidden />
              <span>
                Matched against their answer to <em className="not-italic font-medium">{linkedPrompt}</em>, not to
                this one.
              </span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ImportanceControl
            value={importance}
            disabled={!answered}
            onChange={handleImportance}
          />
          <button
            type="button"
            onClick={handleClear}
            title="Clear this answer"
            aria-label="Clear this answer"
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground/40 transition-all hover:bg-accent hover:text-foreground",
              !answered && "pointer-events-none opacity-0",
            )}
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="relative">
        {/* The track we draw. The input's own track is transparent. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-[calc(50%-5px)] h-2.5 rounded-full bg-muted"
        >
          {answered && shown !== 0 && (
            /* No transition: this follows a finger during a drag, and easing
               it makes the fill lag behind the thumb rather than track it. */
            <div
              className="absolute top-0 h-2.5 rounded-full"
              style={{
                left: `${Math.min(50, pos)}%`,
                width: `${Math.abs(pos - 50)}%`,
                background: color,
                opacity: 0.85,
              }}
            />
          )}
          {/* Centre notch — the "don't care" point. */}
          <div className="absolute left-1/2 top-1/2 h-3.5 w-px -translate-x-1/2 -translate-y-1/2 bg-border" />
        </div>

        {/* Stop markers. */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-[calc(50%-1.5px)]">
          {STOPS.map((s, i) => (
            <span
              key={s}
              className={cn(
                "absolute size-[3px] -translate-x-1/2 rounded-full",
                answered && Math.abs(s) <= Math.abs(shown) && Math.sign(s) === Math.sign(shown)
                  ? "bg-background/70"
                  : "bg-border",
              )}
              style={{ left: `${STOP_POSITIONS[i]}%` }}
            />
          ))}
        </div>

        <input
          id={id}
          ref={ref}
          type="range"
          min={SCALE_MIN}
          max={SCALE_MAX}
          step={1}
          value={shown}
          data-unset={!answered}
          data-question={questionId}
          onChange={handleChange}
          onFocus={handleActivate}
          aria-valuetext={answered ? valueLabel : UNANSWERED_LABEL}
          className="scale-range relative"
          style={{ "--track": "transparent", "--thumb": color } as React.CSSProperties}
        />
      </div>

      <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[12px]">
        <span className="min-w-0 flex-1 truncate text-muted-foreground">
          {negativeLabel}
        </span>
        <span
          className={cn(
            "shrink-0 text-center font-medium tabular-nums",
            !answered && "text-muted-foreground/50",
          )}
          style={answered ? { color } : undefined}
        >
          {answered ? valueLabel : UNANSWERED_LABEL}
        </span>
        <span className="min-w-0 flex-1 truncate text-right text-muted-foreground">
          {positiveLabel}
        </span>
      </div>
    </div>
  );
});
